import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import {  Form, Row, Col, Button, message, Select } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import SmartTable from "./SmartTable";
import { fetchEmployeeHyperfind, getEmployeeList } from "../../services/crew";
import HOC from "./HOC";
import StrictDatePicker from "./StrictDatePicker";
import useAuth from "../../hooks/useAuth";
import { getDetails } from "../../services/auth";
import { FilterOutlined } from "@ant-design/icons";
import { Badge, Modal, Divider, Spin } from "antd";
import { getEmployeeDB, initializeEmployeeDB, sortEmployees } from './sortingUtils';
import ToastErrorMessage from "./ToastErorrMessage";

const FILTER_STORAGE_KEY = "assignEmployeeAppliedFilters";
const FILTER_FORM_STORAGE_KEY = "assignEmployeeFilterFormValues";

dayjs.extend(utc);

const AssignEmployee = forwardRef(
  (
    { basicDetails, initialEmployees = [], onPrev, onNext, selectedDate, mode },
    ref
  ) => {
    const [form] = Form.useForm();
    const [formFilter] = Form.useForm();
    const [workmen, setWorkmen] = useState([]);
    const [loading, setLoading] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);
    const { userConfig } = useAuth();
    const [permissions, setPermissions] = useState([]);
    const [permissionsGeneralAccess, setPermissionsGeneralAccess] = useState([]);
      const [hasHyperfindAccess, setHasHyperfindAccess] = useState(false);
    const [hasDownloadAccess, setHasDownloadAccess] = useState(false);
            const [backDatedEntry, setBackDatedEntry] = useState(false);
    
    const [employeeDB, setEmployeeDB] = useState(null);
    const [tablePageSize, setTablePageSize] = useState(100);

    useEffect(() => {
        const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
        if (!key || typeof window === "undefined") return;
        const stored = window.localStorage.getItem(key);
        const parsed = stored ? parseInt(stored, 10) : NaN;
        if (Number.isFinite(parsed) && parsed > 0) {
          setTablePageSize(parsed);
        }
      });
    const [sortConfig, setSortConfig] = useState({ field: null, order: null });
    const originalWorkmenData = useRef([]);
    const isSortingRef = useRef(false);
    
    

    const getKey = (emp) => `${emp.id}-${emp.currentCrew || "NONE"}`;

    const [selectedRowKeys, setSelectedRowKeys] = useState(
      initialEmployees.map((emp) => getKey(emp))
    );

    const [date, setDate] = useState(selectedDate || basicDetails?.date || dayjs().add(1, "day"));
    const [hyperfindOptions, setHyperfindOptions] = useState([]);
    const [selectedHyperfind, setSelectedHyperfind] = useState(sessionStorage.getItem('selectedHyperfind') || undefined);

    const [modalOpenEmployeeFilter, setModalOpenEmployeeFilter] = useState(false);
    const [showDot, setShowDot] = useState(false);
    const [ssn4, setSsn4] = useState([]);
    const [unionStat, setUnionStat] = useState([]);
    const [trade, setTrade] = useState([]);
    const [union, setUnion] = useState([]);
    const [formValues, setFormValues] = useState({});
    const [isApplyDisabled, setIsApplyDisabled] = useState(true);
    const showSpecific = (parameter) => (permissions || []).find(
        (f) => f.feature && f.feature.toLowerCase().includes(parameter.toLowerCase())
    );

    const subFind = (needle, source) =>
      source?.subFeatures?.find(
        (s) => s?.name && s.name.toString().toLowerCase().includes(needle.toString().toLowerCase())
      );

    const includeFilter = !(subFind('filters', showSpecific('add-ons'))?.access === false);
      
    const handleSort = async (field, order) => {
      
      if (!order) {
        if (originalWorkmenData.current.length > 0) {
          isSortingRef.current = true;
          setWorkmen([...originalWorkmenData.current]);
          setSortConfig({ field: null, order: null });
        }
        return;
      }

      isSortingRef.current = true;
      const sortedData = await sortEmployees(originalWorkmenData.current, field, order, setLoading);
      setWorkmen(sortedData);
      setSortConfig({ field, order });
    };

    const applyDefaultEmployeeSort = async (employees = []) => {
        if (!Array.isArray(employees) || employees.length === 0) return [];
        const sortedEmployees = await sortEmployees(employees, "name", "ascend", setLoading);
        setSortConfig({ field: "name", order: "ascend" });
        return sortedEmployees;
      };
      

    const toOptionArray = (arr) => (arr || []).map(v => ({
        name: v,
        description: ""   
    }));

    const handleModalOpen = () => {
      setModalOpenEmployeeFilter(true);
       setFormValues({});
    };
    
    const handleModalClose = () => {
      setModalOpenEmployeeFilter(false);
      };

    const handleReset = () => {
      formFilter.resetFields();
      setFormValues({});
      setShowDot(false);
      setIsApplyDisabled(true);
      sessionStorage.removeItem(FILTER_STORAGE_KEY);
      sessionStorage.removeItem(FILTER_FORM_STORAGE_KEY);
      
      try {
        fetchEmployeeList(date);
      } catch (err) {
        console.error('Error refetching employee list after reset:', err);
      }
    };

    const handleFormChange = (changedValues, allValues) => {
      setFormValues(allValues);
    };

    
    const handleApplyFilters = async () => {
      try {
        const previouslySelectedEmployees = (workmen || []).filter(emp =>
          selectedRowKeys.includes(getKey(emp))
        );

        const values = await formFilter.validateFields();

        sessionStorage.setItem(
          FILTER_FORM_STORAGE_KEY,
          JSON.stringify(values)
        );

        setLoading(true);
        setShowDot(true);

        const filters = {};

        Object.entries(values).forEach(([key, value]) => {
          if (value === undefined || value === null) return;

          if (key === "nextScheduled") {
            if (value === "has") {
              const nonEmpty = Array.from(
                new Set(
                  (workmen || [])
                    .map(w => w?.nextScheduled)
                    .filter(v => v && String(v).trim() !== "")
                )
              );

              if (nonEmpty.length) filters[key] = nonEmpty;
            } else if (value === "none") {
              filters[key] = null;
            }
            return;
          }

          if (Array.isArray(value)) {
            if (!value.length) return;
            filters[key] = value;
            return;
          }

          if (typeof value === "string") {
            const trimmed = value.trim();
            if (!trimmed) return;
            filters[key] = trimmed;
            return;
          }

          filters[key] = value;
        });

        sessionStorage.setItem(
          FILTER_STORAGE_KEY,
          JSON.stringify(filters)
        );

        await applyEmployeeFilters(filters, date, {
          showSuccess: true,
          closeModal: true,
          previouslySelectedEmployees
        });

      } catch (error) {
        setLoading(false);
        message.error(
          error?.response?.data?.message ||
          error?.message ||
          "Failed to fetch filtered employee data."
        );
      }
    };

    const applyEmployeeFilters = async (
      filters,
      selectedDate,
      options = {}
    ) => {
      const {
        showSuccess = false,
        closeModal = false,
        previouslySelectedEmployees = []
      } = options;

      const stored = sessionStorage.getItem(FILTER_STORAGE_KEY);
      const storedFormValues = sessionStorage.getItem(FILTER_FORM_STORAGE_KEY);
      if (!stored || !storedFormValues) return;

      try {
        setLoading(true);
        setShowDot(true);

        const payload = {
          date: selectedDate
            ? dayjs(selectedDate).format("YYYY-MM-DD")
            : undefined,
          userId: userConfig?.userid,
          filters
        };
        const response = await getEmployeeList(payload);
        const filteredEmployees = response?.data?.employees || [];

        if (!filteredEmployees.length) {
          message.warning(
            response?.data?.message ||
            "No data found for selected filters!"
          );
          setWorkmen([]);
          return;
        }

        const mergedEmployees = [
          ...previouslySelectedEmployees,
          ...filteredEmployees.filter(
            emp =>
              !previouslySelectedEmployees.some(
                sel => getKey(sel) === getKey(emp)
              )
          )
        ];

        const withCrew = mergedEmployees.filter(emp => Boolean(emp.currentCrew));
        const withoutCrew = mergedEmployees.filter(emp => !emp.currentCrew);

        const sortByLastName = (a, b) =>
          (a.lastName || "")
            .toLowerCase()
            .localeCompare((b.lastName || "").toLowerCase());

        withCrew.sort(sortByLastName);
        withoutCrew.sort(sortByLastName);

        setWorkmen([...withCrew, ...withoutCrew]);
        setAppliedFilters(filters);

        if (showSuccess) {
          message.success("Employee Details filtered successfully!");
        }

        if (closeModal) {
          setModalOpenEmployeeFilter(false);
        }

      } catch (err) {
        console.error("applyEmployeeFilters error:", err);
        message.error("Failed to fetch filtered employee data.");
      } finally {
        setLoading(false);
      }
    };

    useEffect(() => {
      const hasAnyValue = Object.values(formValues).some((value) => {
        if (value === undefined || value === null) return false;
        if (Array.isArray(value)) return value.length > 0; 
        if (typeof value === 'string') return value.trim() !== '';
        if (typeof value === 'object') return Object.keys(value).length > 0; 
          return true; 
        });
      setIsApplyDisabled(!hasAnyValue);
      }, [formValues]);

    useEffect(() => {
      getDetails(userConfig.orgId)
        .then((response) => {
          const role = response.data.find(
            (r) =>
              r?.selectedRole?.toLowerCase().trim() ===
                userConfig.role?.toLowerCase().trim() &&
              r?.selectedRoleType?.toLowerCase().trim() ===
                userConfig.roleName?.selectedRoleType?.toLowerCase().trim()
          );
          setPermissions(role.permissions.cap.crewScheduling);
           setPermissionsGeneralAccess(role?.permissions?.cap?.generalAccess);

        })
        .catch((err) => console.error(err));
    }, []);

    useEffect(() => {
      const hyperfindAccess =
        permissionsGeneralAccess?.some(
          perm =>
            perm.feature === "Hyperfind Access" &&
            perm.access === true
        ) || false;
    
      const downloadAccess =
        permissionsGeneralAccess?.some(
          perm =>
            perm.feature === "Download" &&
            perm.access === true
        ) || false;

         const backdatedEntry =
      permissionsGeneralAccess?.some(
        perm =>
          perm.feature === "Backdated Entry" &&
          perm.access === true
      ) || false;
    
      setHasHyperfindAccess(hyperfindAccess);
      setHasDownloadAccess(downloadAccess);
          setBackDatedEntry(backdatedEntry);

    }, [permissionsGeneralAccess]);

    const crewPerm = permissions.find(
      (p) => p.feature === "Crew & Schedule Management"
    );
    const schedulePerm = crewPerm?.subFeatures?.find(
      (sf) => sf.name === "Schedule Management"
    );
    const canCreateCrew =
      schedulePerm && schedulePerm.create && schedulePerm.create !== "None";

    const employeeColumns = useMemo(() => {
      const cols = HOC("employee", { 
        crewSchedulingPermissions: permissions,
        date,
        onSort: handleSort,
        sortConfig
      });
      return cols.map((col) => ({
        ...col,
        onHeaderCell: () => ({
          style: {
            backgroundColor: "#EAEEFF",
            color: "black",
            borderRight: "1px solid #d9d9d9",
            fontWeight: "bold",
          },
        }),
      }));
    }, [date, basicDetails?._id, sortConfig]);

    useEffect(() => {
      if (workmen && workmen.length > 0) {
        if (!isSortingRef.current) {
          originalWorkmenData.current = JSON.parse(JSON.stringify(workmen));
        } else {
          isSortingRef.current = false;
        }
        
        const db = initializeEmployeeDB(workmen);
        setEmployeeDB(db);
      }
    }, [workmen]);

    const fetchEmployeeList = async (d) => {
      setLoading(true);
      const filters = {};
      const stored = JSON.parse(sessionStorage.getItem(FILTER_STORAGE_KEY));
      const values = sessionStorage.getItem(FILTER_FORM_STORAGE_KEY);
      
      if (selectedHyperfind) {
        filters.hyperfind = selectedHyperfind;
      }
      try {
        const payload = {
          date: d ? dayjs(d).format("YYYY-MM-DD") : undefined,
          userId: userConfig?.userid,
          filters,
        };
        
        const response = await getEmployeeList(payload);
        const employees = response?.data?.employees || [];
        const keysToSelect = employees
          .map((emp) => getKey(emp))
          .filter((key) => selectedRowKeys.includes(key));
      //     const sortedEmployees = [...(response?.data?.employees || [])].sort((a, b) => {
      //     const aSelected = keysToSelect.includes(getKey(a));
      //     const bSelected = keysToSelect.includes(getKey(b));
      //  // 1. Selected on top
      //     if (aSelected !== bSelected) {
      //       return bSelected - aSelected;
      //     }
      //    const aCrew = Boolean(a.currentCrew);
      //     const bCrew = Boolean(b.currentCrew);
      //    // 2. Then crew members
      //     if (aCrew !== bCrew) {
      //       return bCrew - aCrew;
      //     }
      //  // 3. Alphabetical by last name
      //     return (a.lastName ?? '').localeCompare(b.lastName ?? '', undefined, {
      //       sensitivity: 'base'
      //     });
      //   });
       
        const sortedEmployees = await applyDefaultEmployeeSort(response?.data?.employees || []);
        setWorkmen(sortedEmployees);
        setSsn4(toOptionArray(response?.data?.employees?.map(e => e.ssn4)?.filter(Boolean)?.sort((a,b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }))));
        setUnionStat(toOptionArray(response?.data?.employees?.map(e => e.unionStat)?.filter(Boolean)?.sort((a,b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }))));
        setTrade(toOptionArray(response?.data?.employees?.map(e => e.trade)?.filter(Boolean)?.sort((a,b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }))));
        setUnion(toOptionArray(response?.data?.employees?.map(e => e.union)?.filter(Boolean)?.sort((a,b) => String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true }))));
        setSelectedRowKeys(keysToSelect);
        if (stored) {
          setShowDot(true);
          if (values) formFilter.setFieldsValue(JSON.parse(values));
          applyEmployeeFilters(stored, d, { previouslySelectedEmployees: employees.filter(emp => keysToSelect.includes(getKey(emp))) });
        }
      } catch (error) {
        console.error("Error fetching workmen:", error);
        ToastErrorMessage(true);
        setWorkmen([])
      } finally {
        setLoading(false);
      }
    };
 useEffect(() => {
      if (selectedDate) {
        setDate(selectedDate);
        form.setFieldsValue({ date: selectedDate });
        // fetchEmployeeList(selectedDate);
      }
      fetchEmployeeList(date)
      fetchHyperFinds();
    }, [selectedDate, selectedHyperfind]);
useEffect(() => {
      if (!selectedDate) {
        const defaultDate = basicDetails?.date
          ? dayjs(basicDetails.date, "YYYY-MM-DD")
          : dayjs().add(1, "day");
        setDate(defaultDate);
        form.setFieldsValue({ date: defaultDate });
        fetchEmployeeList(defaultDate);
      }
    }, [basicDetails?.date]);
const handleDateChange = (d) => {
    setDate(d);
      form.setFieldsValue({ date: d });
      fetchEmployeeList(d);
    };
 const initialKeys = useMemo(
      () => initialEmployees.map((emp) => getKey(emp)),
      [initialEmployees]
    );
 useImperativeHandle(ref, () => ({
 getSelectedEmployees: () => {
        const selectedEmployees = workmen.filter((emp) =>
          selectedRowKeys.includes(getKey(emp))
        );
        const d = form.getFieldValue("date");

        if (!selectedEmployees.length)
          return message.warning("Please select at least one employee.");

        if (!d) return message.warning("Date is required.");

        const flagged = selectedEmployees.map((emp) => ({
          ...emp,
          changeFlag: initialKeys.includes(getKey(emp))
            ? "untouched"
            : "added",
        }));
const removed = initialEmployees
          .filter(
            (emp) =>
              !selectedEmployees.some((sel) => getKey(sel) === getKey(emp))
          )
          .map((emp) => ({ ...emp, changeFlag: "removed" }));

        return {
          valid: true,
          employees: flagged,
          removed,
          date: d.format("YYYY-MM-DD"),
        };
      },
    }));
const handleNext = () => {
  if (!canCreateCrew) {
    message.error("You don't have permission to Assign Employee.");
    return;
  }

  const selectedEmployees = workmen.filter((emp) =>
    selectedRowKeys.includes(getKey(emp))
  );
const d = form.getFieldValue("date");
 const flagged = selectedEmployees.map((emp) => ({
    ...emp,
    changeFlag: initialKeys.includes(getKey(emp))
      ? "untouched"
      : "added",
  }));
const removed = initialEmployees
    .filter(
      (emp) =>
        !selectedEmployees.some((sel) => getKey(sel) === getKey(emp))
    )
    .map((emp) => ({ ...emp, changeFlag: "removed" }));

  if (!selectedEmployees.length) {
    message.warning({
      content: "No employee selected.",
      duration: 2
    });

    setTimeout(() => {
      onNext({
        employees: flagged,
        removed,
        date: d.format("YYYY-MM-DD"),
        employeeAssignedEdit: basicDetails?.employeeAssignedEdit,
      });
    }, 3000);

    return;
  }

  onNext({
    employees: flagged,
    removed,
    date: d.format("YYYY-MM-DD"),
    employeeAssignedEdit: basicDetails?.employeeAssignedEdit,
  });
};
 const rowSelection = {
      selectedRowKeys,
      onChange: (keys) => {
      const visibleKeys = new Set(workmen.map(emp => getKey(emp)));
      const currentVisibleSelections = selectedRowKeys.filter(key => visibleKeys.has(key));
      const newVisibleSelections = keys.filter(key => visibleKeys.has(key));
        const deselectedKeys = new Set(currentVisibleSelections.filter(key => !newVisibleSelections.includes(key)));
        const mergedKeys = selectedRowKeys.filter(key => !deselectedKeys.has(key)).concat(newVisibleSelections.filter(key => !selectedRowKeys.includes(key)));
        setSelectedRowKeys(mergedKeys);
      },
    };
const totalNeeded = Number(basicDetails?.employeesNeeded) || 0;
const totalSelected = selectedRowKeys.length || 0;
    const totalDiff = totalSelected - totalNeeded;
    const fetchHyperFinds = () => {
      fetchEmployeeHyperfind().then((res) => {
        // setHyperfindOptions(res.data);
        const raw = res?.data;
        const normalized = Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.hyperfinds)
              ? raw.hyperfinds
            : [];
        setHyperfindOptions(normalized);
      })
      .catch((err) => {
        console.error('Failed to fetch employee hyperfinds:', err);
        setHyperfindOptions([]);
      });
    }
// useEffect(() => {
//       const stored = sessionStorage.getItem('selectedHyperfind');
//       if (stored) {
//         setSelectedHyperfind(stored);
//       }
//     }, []);

    return ( <div>
      <Spin spinning={loading} fullscreen size="large" />
      <Form layout="vertical" form={form}>
        <Row gutter={[4, 0]} align="middle" justify="space-between" style={{ marginBottom: "20px", flexWrap: 'nowrap' }}>
          <Col>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: "#555", marginBottom: 6, fontWeight: 800 }}>Date</label>
              <Form.Item
                name="date"
                rules={[{ required: true, message: "Date is required" }]}
                style={{ marginBottom: 0 }}
                onMouseDown={(e) => {e.preventDefault();}}
              >
                <StrictDatePicker
                  value={date}
                  format="MM-DD-YYYY"
                  style={{ width: 120 }}
                  onChange={handleDateChange}
                  // disabled={mode === "edit"}
                  disabled={true}

                  disabledDate={(current) => {
                if (backDatedEntry) return false;
    return current.startOf('day').isBefore(dayjs().startOf('day'));
  }}
                />
              </Form.Item>
            </div>
          </Col>

          {/* Hyperfind */}
          <Col>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: "#555", marginBottom: 6, fontWeight: 800 }}>Hyperfind</label>
              <Select
                placeholder="Select"
                value={selectedHyperfind}
                disabled={!hasHyperfindAccess}
                onChange={(val) => {
                  setSelectedHyperfind(val);
                  if (val) {
                    sessionStorage.setItem('selectedHyperfind', val);
                  } else {
                    sessionStorage.removeItem('selectedHyperfind');
                  }
                }}
                options={(Array.isArray(hyperfindOptions) ? hyperfindOptions : []).map(opt => ({
                  value: opt?.qualifier ?? opt?.value ?? '',
                  label: opt?.qualifier ?? opt?.label ?? '',
                })).filter(opt => opt.value && opt.label)}
                allowClear
                style={{ width: 180 }}
              />
            </div>
          </Col>

          {/* Crew Context */}
          <Col>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: "#555", marginBottom: 6, fontWeight: 800 }}>Crew Context</label>
              <div style={{ backgroundColor: '#E3F8FF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', height: 32, gap: 8 }}>
                <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>Crew Name:</span>
                <strong style={{ fontSize: 13, color: '#000', display: 'inline-block', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={basicDetails?.crewName}>
                  {basicDetails?.crewName}
                </strong>
              </div>
            </div>
          </Col>
          
          <Col>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: "#555", marginBottom: 6, fontWeight: 800, visibility: 'hidden' }}>Crew Lead</label>
              <div style={{ backgroundColor: '#E3F8FF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', height: 32, gap: 8 }}>
                <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>Crew Lead:</span>
                <strong style={{ fontSize: 13, color: '#000', display: 'inline-block', maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={basicDetails?.crewLead}>
                  {basicDetails?.crewLead}
                </strong>
              </div>
            </div>
          </Col>
          
          <Col>
            <div>
              <label style={{ display: 'block', fontSize: 12, color: "#555", marginBottom: 6, fontWeight: 800, visibility: 'hidden' }}>Event Name</label>
              <div style={{ backgroundColor: '#E3F8FF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', height: 32, gap: 8 }}>
                <span style={{ fontSize: 12, color: '#666', whiteSpace: 'nowrap' }}>Event Name:</span>
                <strong style={{ fontSize: 13, color: '#000', display: 'inline-block', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={basicDetails?.eventDescription}>
                  {basicDetails?.eventDescription}
                </strong>
              </div>
            </div>
          </Col>
        </Row>

        {/* Line 2: Employees Header and Stats */}
        <Row align="middle" style={{ marginBottom: "12px" }}>
          <Col flex="none">
            <label style={{ fontSize: 13, fontWeight: 700, color: '#333', margin: 0 }}>Employees</label>
          </Col>
          <Col flex="auto" style={{ display: 'flex', justifyContent: 'center' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 10, border: '1px solid #1890ff' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Total Needed:</span>
                <strong style={{ fontSize: 13, color: '#000' }}>{totalNeeded}</strong>
              </div>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 10, border: '1px solid #1890ff' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Total Scheduled:</span>
                <strong style={{ fontSize: 13, color: '#000' }}>{totalSelected}</strong>
              </div>
              <div style={{ backgroundColor: '#FFFFFF', borderRadius: 6, padding: '8px 16px', display: 'inline-flex', alignItems: 'center', gap: 10, border: '1px solid #1890ff' }}>
                <span style={{ fontSize: 12, color: '#666' }}>Remaining:</span>
                <strong style={{ fontSize: 13, color: '#000' }}>{totalDiff}</strong>
              </div>
            </div>
          </Col>
          <Col flex="none">
            <Badge dot={showDot} color='#1890ff' offset={[-1, 1]} style={{height:'10px', width:'10px'}}>
              <Button
                type="link"
                icon={<FilterOutlined />}
                className="crew-header-filter"
                disabled={!includeFilter}
                onClick={handleModalOpen}
                style={{
                  border: "1px solid #91d5ff",
                  color: "#1890ff",
                  backgroundColor: "white",
                }}
              />
            </Badge>
          </Col>
        </Row>

        <SmartTable
          className="table-style"
          tableKey="assign-employee"
          columns={employeeColumns}
          dataSource={workmen}
          pagination={{
            pageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          onChange={(pagination, filters, sorter, extra) => {
            if (pagination && pagination.pageSize) {
              setTablePageSize(pagination.pageSize);
              const key = userConfig?.userid ? `employee-table-page-size-${userConfig.userid}` : null;
              if (key && typeof window !== "undefined") {
                window.localStorage.setItem(key, String(pagination.pageSize));
              }
            }
          }}
          // scroll={{ x: "max-content", y: 455 }}
          scroll={workmen?.length ? { x: "100%", y: 455 } : { y: 455 } }
          bordered
          loading={loading}
          rowSelection={rowSelection}
          rowKey={getKey}
        />

        <Row justify="end" style={{ marginTop: 16 }}>
          <Col>
            <Button
              onClick={onPrev}
              style={{ marginRight: 10 }}
              disabled={mode === "edit"}
            >
              Previous
            </Button>
          </Col>

          <Col>
            <Button type="primary" onClick={handleNext}>
              Next
            </Button>
          </Col>
        </Row>

        <style jsx>{`
          .info-box {
            background-color: #e3f8ff;
            border-radius: 6px;
            padding: 5px 8px;
            min-width: 130px;
          }
        `}</style>
      </Form>

      {/* Filter Modal */}
        <Modal
          title={
            <div style={{ marginLeft: '-15px',marginTop: '-10px' ,paddingBottom: '-100px' ,display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: "100%" }}>
                <span>Filter</span>
                <Button 
                 type="link" 
                 onClick={handleReset}
                 style={{ paddingLeft: '30px', fontWeight: 'normal', paddingRight:'8px'}}
                ><u>Reset</u></Button>
            </div>
          }
          open={modalOpenEmployeeFilter}
          onCancel={handleModalClose}
          maskClosable={false}
          keyboard={false}
          footer={null}
          width={370}
        >
            <div style={{margin: '-15px', marginRight:'-24px', marginLeft: '-24px'}}>
                <Divider style={{ borderColor: '#ccc' }}></Divider>
            </div>
          <Form 
            layout="vertical" 
            name="employeeFilterForm" 
            form={formFilter}
            style={{ paddingTop: "10px" }}
            onValuesChange={handleFormChange}
            
          >
        <span style={{fontWeight: 'bolder',color: 'gray'}}>Employee Filter</span>
         <Form.Item
                label="SSN4"
                name="ssn4"
                style={{ marginBottom: "12px" }}
              >
                <Select placeholder="Select SSN4" allowClear showSearch mode='multiple'>
                    {(ssn4 || []).filter((v, i, a) => {
                        const key = typeof v === 'string' ? v : v?.name;
                        return key !== undefined && a.findIndex(x => (typeof x === 'string' ? x : x?.name) === key) === i;
                    }).map((t) => <Option key={(typeof t === 'string' ? t : t.name)} value={(typeof t === 'string' ? t : t.name)}>{(typeof t === 'string' ? t : (t.name + (t.description ? ` - ${t.description}` : "")))}</Option>)}
                </Select>
              </Form.Item>

            <Form.Item
              label="Union Stat"
              name="unionStat"
              style={{ marginBottom: "12px" }}
            >
              <Select placeholder="Select Union Stat" allowClear showSearch mode='multiple'>
                  {(unionStat || []).filter((v, i, a) => {
                      const key = typeof v === 'string' ? v : v?.name;
                      return key !== undefined && a.findIndex(x => (typeof x === 'string' ? x : x?.name) === key) === i;
                  }).map((t) => <Option key={(typeof t === 'string' ? t : t.name)} value={(typeof t === 'string' ? t : t.name)}>{(typeof t === 'string' ? t : (t.name + (t.description ? ` - ${t.description}` : "")))}</Option>)}
              </Select>
            </Form.Item>

            <Form.Item
              label="Trade Class/Skill Set"
              name="trade"
              style={{ marginBottom: "12px" }}
            >
              <Select placeholder="Select Trade Cllass/Skill Set" allowClear showSearch mode='multiple'>
                  {(trade || []).filter((v, i, a) => {
                      const key = typeof v === 'string' ? v : v?.name;
                      return key !== undefined && a.findIndex(x => (typeof x === 'string' ? x : x?.name) === key) === i;
                  }).map((t) => <Option key={(typeof t === 'string' ? t : t.name)} value={(typeof t === 'string' ? t : t.name)}>{(typeof t === 'string' ? t : (t.name + (t.description ? ` - ${t.description}` : "")))}</Option>)}  
              </Select>
            </Form.Item>

            <Form.Item 
              label="Union" 
              name="union" 
              style={{ marginBottom: "16px" }}
            >
              <Select placeholder="Select Union" allowClear showSearch mode='multiple'>
                {(union || []).filter((v, i, a) => {
                  const key = typeof v === 'string' ? v : v?.name;
                  return key !== undefined && a.findIndex(x => (typeof x === 'string' ? x : x?.name) === key) === i;
                }).map((t) => <Option key={(typeof t === 'string' ? t : t.name)} value={(typeof t === 'string' ? t : t.name)}>{(typeof t === 'string' ? t : t.name)}</Option>)}  
              </Select>

            </Form.Item>
            <Divider style={{ borderColor: '#ccc' }}></Divider>
                <span style={{fontWeight: 'bolder',color: 'gray'}}>General Filter</span>

            <Form.Item
              label="On Premises"
              name="onPremises"
              style={{ marginBottom: "12px" }}
            >
              <Select
                placeholder="Select On Premises"
                allowClear
              >
               <Option value={true}>Yes</Option>
               <Option value={false}>No</Option> 
              </Select>
            </Form.Item>

            <Form.Item
              label="Scheduled"
              name="dispatched"
              style={{ marginBottom: "12px" }}
            >
<Select
                placeholder="Select Scheduled"
                allowClear
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>

            <Form.Item
              label="Multi Scheduled"
              name="multischeduled"
              style={{ marginBottom: "12px" }}
            >
<Select
                placeholder="Select Multi Scheduled"
                allowClear
              >
               <Option value={true}>Yes</Option>
               <Option value={false}>No</Option> 
              </Select>
            </Form.Item>

            <Form.Item
              label="Show Next Scheduled"
              name="nextScheduled"
              style={{ marginBottom: "16px" }}
            >
              <Select
                placeholder="Select Show Next Scheduled"
                allowClear
              >
                <Option value={"has"}>Yes</Option>
                <Option value={"none"}>No</Option>
              </Select>
            </Form.Item>
              <Button 
              type="primary" 
              block 
              onClick={handleApplyFilters}
              disabled={isApplyDisabled}
            >
              {loading ? <Spin size="small" /> : "Apply"}
            </Button>
          </Form>
        </Modal>   
      </div>
    );
  }
);

export default AssignEmployee;
 